import {
  Add,
  Work,
  PictureAsPdf,
  Lock,
  LockOpen,
  Group,
  Event,
  Print,
} from "@mui/icons-material";

import { useTheme } from "@mui/material/styles";
import { memo } from "react";
import { useSondertagContext } from "../context/sondertagContext";
import { useAppContext } from "../../../2_ksbHub/context/appContext";
import KsbAppBar from "../../../2_ksbHub/components/KsbAppBar";
import KsbActionButton from "../../../2_ksbHub/components/KsbActionButton";
import KsbToolbarAdmin from "../../../2_ksbHub/components/KsbToolbarAdmin";
import KsbToolbarUser from "../../../2_ksbHub/components/KsbToolbarUser";
import KsbSelector from "../../../2_ksbHub/components/KsbSelector";
import SondertagLockChip from "./SondertagLockChip";
import SondertagLockInfo from "./SondertagLockInfo";
import useViewOptions from "../hooks/useViewOptions";
import useSondertagPDF from "../hooks/useSondertagPDF";
import KsbAppToolbarContainer from "../../../2_ksbHub/components/KsbAppToolbarContainer";
import SondertagStatsDisplay from "./SondertagStatsDisplay";
import SondertagRankingButton from "./SondertagRankingButton";
import SondertagPensenButton from "./SondertagPensen/SondertagPensenButton";

function SondertagHeader({ viewMode, setViewMode, dialogs }) {
  const theme = useTheme();

  const { isTeacherView, isMobile } = useAppContext();

  const {
    schoolyears,
    selectedSchoolyear,
    setSelectedSchoolyear,
    sonderEvents,
    selectedSonderEvent,
    setSelectedSonderEvent,
    isASondertageEventAdmin,
  } = useSondertagContext();

  const { generatePDF, generateParticipantPDF } = useSondertagPDF();
  const viewOptions = useViewOptions();

  const hasAnyLock =
    selectedSonderEvent?.lockForStudents ||
    selectedSonderEvent?.lockForTeachers;

  return (
    <KsbAppBar>
      <KsbAppToolbarContainer isAdmin={isASondertageEventAdmin}>
        {/* ✅ USER SIDE */}
        <KsbToolbarUser>
          {/* ✅ VIEW MODE (now with KsbSelector ✅) */}
          <KsbSelector
            value={viewMode}
            options={viewOptions}
            getValue={(v) => v.id}
            getLabel={(v) => v.label}
            onChange={(value) => {
              setViewMode(value);
            }}
          />

          {/* ✅ STATS VIEW*/}
          <SondertagStatsDisplay />
          <SondertagRankingButton />
          <SondertagPensenButton />

          {/* ✅ EVENT SELECTOR */}
          {sonderEvents.length > 0 && (
            <KsbSelector
              value={selectedSonderEvent?.id}
              options={sonderEvents}
              getValue={(e) => e.id}
              getLabel={(e) => (isMobile ? e.label.slice(0, 6) : e.label)}
              getExtra={(sondertag) => (
                <SondertagLockInfo roles={sondertag.roles} />
              )}
              onChange={(id) => {
                const ev = sonderEvents.find((x) => x.id === id);
                if (ev) setSelectedSonderEvent(ev);
              }}
            />
          )}

          {/* ✅ SCHOOLYEAR SELECTOR*/}
          {schoolyears.length > 0 && (
            <KsbSelector
              value={selectedSchoolyear?.id}
              options={schoolyears}
              getValue={(y) => y.id}
              getLabel={(y) => (isMobile ? y.label.slice(2) : y.label)}
              onChange={(id) => {
                const sy = schoolyears.find((y) => y.id === id);
                if (sy) setSelectedSchoolyear(sy);
              }}
            />
          )}

          {!isTeacherView && <SondertagLockChip />}
        </KsbToolbarUser>

        {/* ✅ ADMIN OPTIONS */}
        {isASondertageEventAdmin && !isTeacherView && (
          <KsbToolbarAdmin>
            <KsbActionButton
              onClick={() => dialogs.setCreateSonderEventOpen(true)}
              icon={<Event />}
              label="Neuer Event"
              color={theme.palette.eventManager.slot.mine}
            />

            <KsbActionButton
              onClick={() => dialogs.setCreateSondertagOpen(true)}
              icon={<Add />}
              label="Neuer Sondertag"
            />

            <KsbActionButton
              onClick={() => dialogs.setCreateJobOpen(true)}
              icon={<Work />}
              label="Neuer Klassensatz"
            />

            <KsbActionButton
              onClick={generatePDF}
              icon={<PictureAsPdf />}
              desktopIcon={<Print />}
              label="Übersicht"
              color={theme.palette.text.primary}
            />

            <KsbActionButton
              onClick={generateParticipantPDF}
              icon={<Group />}
              desktopIcon={<Print />}
              label="LP Liste"
            />

            <KsbActionButton
              onClick={() => {
                dialogs.setLockDialogOpen(true);
              }}
              icon={hasAnyLock ? <LockOpen /> : <Lock />}
              label={hasAnyLock ? "Freigeben" : "Sperren"}
              color={
                hasAnyLock
                  ? theme.palette.success.main
                  : theme.palette.warning.main
              }
            />
          </KsbToolbarAdmin>
        )}
      </KsbAppToolbarContainer>
    </KsbAppBar>
  );
}

export default memo(SondertagHeader);
